import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal, X, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ACCENT = {
  sneakers: "01", wigs: "02", couture: "03", epicerie: "04", patisserie: "05", printing: "06",
};

const Collection = ({ category }) => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [filterMeta, setFilterMeta] = useState({ brands: [], subcategories: [] });
  const [filters, setFilters] = useState({ brand: "", subcategory: "", sort: "newest" });
  const [showMobile, setShowMobile] = useState(false);
  const [loading, setLoading] = useState(true);

  const meta = {
    division: CAT_ACCENT[category] || "01",
    title: t(`cat.${category}.title`),
    accent: t(`cat.${category}.accent`),
    desc: t(`cat.${category}.desc`),
    label: t(`cat.${category}.label`),
  };

  useEffect(() => {
    axios.get(`${API}/products/filters/meta?category=${category}`).then((r) => setFilterMeta(r.data));
  }, [category]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ category, sort: filters.sort });
    if (filters.brand) params.append("brand", filters.brand);
    if (filters.subcategory) params.append("subcategory", filters.subcategory);
    axios
      .get(`${API}/products?${params}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, filters]);

  // reset filters when category changes
  useEffect(() => {
    setFilters({ brand: "", subcategory: "", sort: "newest" });
  }, [category]);

  const reset = () => setFilters({ brand: "", subcategory: "", sort: "newest" });
  const activeCount = useMemo(() => (filters.brand ? 1 : 0) + (filters.subcategory ? 1 : 0), [filters]);

  return (
    <div data-testid={`collection-page-${category}`} className="pt-32 pb-20">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-4">{t("collection.division")} {meta.division} · {meta.label}</p>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter text-white leading-[0.95]">
              {meta.title} <span className="italic text-[#C5A059]">{meta.accent}</span>.
            </h1>
            {category === "sneakers" && (
              <p
                data-testid="sneakers-tagline"
                className="mt-6 font-display italic text-2xl md:text-3xl text-[#C5A059] leading-tight"
              >
                « {t("cat.sneakers.tagline")} »
              </p>
            )}
            {category !== "sneakers" && (
              <p
                data-testid={`${category}-tagline`}
                className="mt-6 font-display italic text-2xl md:text-3xl text-[#C5A059] leading-tight"
              >
                « {t(`cat.${category}.tagline`)} »
              </p>
            )}
            <p className="mt-6 text-[#A1A1AA] max-w-xl">{meta.desc}</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href="https://wa.me/243981962762"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collection-whatsapp"
              className="btn-outline"
            >
              <MessageCircle size={14} /> {t("collection.askAdvice")}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Filters */}
          <aside className="hidden md:block md:col-span-3 lg:col-span-3">
            <div className="sticky top-28 border-r border-white/10 pr-6 space-y-10">
              {filterMeta.brands?.length > 0 && (
                <FilterGroup t={t} title={t("collection.filterBrand")} options={filterMeta.brands} value={filters.brand} onChange={(v) => setFilters((f) => ({ ...f, brand: v }))} testIdPrefix="filter-brand" />
              )}
              {filterMeta.subcategories?.length > 0 && (
                <FilterGroup t={t} title={t("collection.filterCategory")} options={filterMeta.subcategories} value={filters.subcategory} onChange={(v) => setFilters((f) => ({ ...f, subcategory: v }))} testIdPrefix="filter-subcategory" />
              )}
              {activeCount > 0 && (
                <button onClick={reset} data-testid="filter-reset-button" className="text-xs text-[#C5A059] uppercase tracking-[0.2em] font-grotesk link-underline">
                  {t("collection.reset")} ({activeCount})
                </button>
              )}
            </div>
          </aside>

          {/* Products */}
          <div className="md:col-span-9 lg:col-span-9">
            <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-5">
              <span data-testid="results-count" className="font-grotesk text-sm text-white/70">
                {loading ? t("collection.loading") : `${products.length} ${products.length !== 1 ? t("collection.products") : t("collection.product")}`}
              </span>
              <div className="flex items-center gap-4">
                <button data-testid="open-mobile-filters" onClick={() => setShowMobile(true)} className="md:hidden flex items-center gap-2 text-sm text-white/80">
                  <SlidersHorizontal size={14} /> {t("collection.filters")} {activeCount ? `(${activeCount})` : ""}
                </button>
                <select
                  data-testid="sort-select"
                  value={filters.sort}
                  onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                  className="bg-transparent text-white text-sm uppercase tracking-[0.18em] font-grotesk border-b border-white/20 pb-1 pr-6 focus:outline-none focus:border-[#C5A059] cursor-pointer"
                >
                  <option value="newest" className="bg-[#0a0a0a]">{t("collection.sortNewest")}</option>
                  <option value="price_asc" className="bg-[#0a0a0a]">{t("collection.sortPriceAsc")}</option>
                  <option value="price_desc" className="bg-[#0a0a0a]">{t("collection.sortPriceDesc")}</option>
                  <option value="name" className="bg-[#0a0a0a]">{t("collection.sortName")}</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#0f0f0f] animate-pulse" />)}
              </div>
            ) : products.length === 0 ? (
              <div data-testid="no-products" className="py-32 text-center">
                <p className="font-display text-3xl text-white mb-4">{t("collection.noProducts")}</p>
                <p className="text-[#A1A1AA] mb-8">{t("collection.noProductsDesc")}</p>
                <a href="https://wa.me/243981962762" target="_blank" rel="noopener noreferrer" className="btn-gold">
                  WhatsApp Suzel
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p, idx) => <ProductCard t={t} key={p.id} product={p} index={idx} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {showMobile && (
        <div data-testid="mobile-filters-drawer" className="fixed inset-0 z-50 bg-[#050505] p-6 overflow-y-auto md:hidden">
          <div className="flex items-center justify-between mb-10">
            <h3 className="font-display text-2xl text-white">{t("collection.filters")}</h3>
            <button onClick={() => setShowMobile(false)} data-testid="close-mobile-filters" className="text-white p-2"><X size={22} /></button>
          </div>
          <div className="space-y-10">
            {filterMeta.brands?.length > 0 && (
              <FilterGroup t={t} title={t("collection.filterBrand")} options={filterMeta.brands} value={filters.brand} onChange={(v) => setFilters((f) => ({ ...f, brand: v }))} testIdPrefix="mfilter-brand" />
            )}
            {filterMeta.subcategories?.length > 0 && (
              <FilterGroup t={t} title={t("collection.filterCategory")} options={filterMeta.subcategories} value={filters.subcategory} onChange={(v) => setFilters((f) => ({ ...f, subcategory: v }))} testIdPrefix="mfilter-subcategory" />
            )}
            <div className="pt-6 flex gap-4">
              <button onClick={reset} className="btn-outline flex-1">{t("collection.reset")}</button>
              <button onClick={() => setShowMobile(false)} className="btn-gold flex-1">{t("collection.apply")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterGroup = ({ title, options, value, onChange, testIdPrefix }) => (
  <div>
    <p className="overline mb-4">{title}</p>
    <ul className="space-y-2.5">
      <li>
        <button data-testid={`${testIdPrefix}-all`} onClick={() => onChange("")} className={`text-sm text-left ${value === "" ? "text-[#C5A059]" : "text-white/60 hover:text-white"} transition-colors`}>
          Tous
        </button>
      </li>
      {options.map((opt) => (
        <li key={opt}>
          <button data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onChange(opt)} className={`text-sm text-left ${value === opt ? "text-[#C5A059]" : "text-white/60 hover:text-white"} transition-colors`}>
            {opt}
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const ProductCard = ({ t, product, index }) => (
  <Link
    to={product.category === "sneakers" ? `/sneakers/${product.slug}` : `/produit/${product.slug}`}
    data-testid={`product-card-${product.slug}`}
    className="group block bg-[#0f0f0f] border border-white/5 hover:border-[#C5A059]/30 transition-all duration-500"
    style={{ animation: `fade-up 0.7s ${index * 0.06}s both` }}
  >
    <div className="relative aspect-square bg-[#050505] overflow-hidden flex items-center justify-center p-6">
      <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1000ms]" />
      {product.is_limited && (
        <span className="absolute top-4 left-4 bg-[#C5A059] text-black text-[10px] px-2 py-1 font-grotesk uppercase tracking-[0.16em] font-semibold">
          {t("collection.limited")}
        </span>
      )}
      {product.is_new && !product.is_limited && (
        <span className="absolute top-4 left-4 border border-[#C5A059] text-[#C5A059] text-[10px] px-2 py-1 font-grotesk uppercase tracking-[0.16em]">
          {t("collection.new")}
        </span>
      )}
    </div>
    <div className="p-5">
      <p className="overline text-[10px] mb-2">{product.brand || product.subcategory || "—"}</p>
      <h3 className="font-display text-xl text-white leading-tight mb-3 group-hover:text-[#C5A059] transition-colors">
        {product.name}
      </h3>
      <div className="flex items-center justify-between">
        <span className="text-xs text-white/50">{product.subcategory}</span>
        <span className="font-grotesk text-[#C5A059]">
          {product.on_request ? t("collection.onRequest") : `${product.price.toLocaleString("fr-FR")} ${product.currency || "$"}`}
        </span>
      </div>
    </div>
  </Link>
);

export default Collection;
